import type { RouteObject } from 'react-router'

import { HUB_DETAIL_SEGMENT } from 'features/admin/features/data-integrity/paths'
import { FEATURE_FLAG_DETAIL_SEGMENT } from 'features/admin/features/feature-flags/paths'
import {
  EXPORTS_URL,
  INCOMPLETE_PROJECTS_URL,
  RATINGS_ANOMALIES_URL,
  RATINGS_ANOMALY_DETAIL_SEGMENT,
  URL_SUBMISSIONS_URL,
  VENDOR_DELIVERIES_URL,
} from 'features/admin/features/feed-audit/paths'
import {
  ITEMEYES_SCREEN_SEGMENT,
  ITEMEYES_URL,
} from 'features/admin/features/itemeyes/paths'
import {
  FAMILY_CREATE_SEGMENT,
  FAMILY_DETAIL_SEGMENT,
} from 'features/admin/features/nlp-prompt-catalog/paths'
import {
  RBAC_URL,
  RBAC_SCOPES_URL,
  RBAC_ASSIGNMENTS_URL,
} from 'features/admin/features/rbac/paths'
import { SOURCE_CATALOG_URL } from 'features/admin/features/source-catalog/paths'
import {
  SOURCES_URL,
  SOURCE_CHANNELS_URL,
  SOURCE_PROVIDERS_URL,
  SOURCE_CHANNEL_TYPES_URL,
  SOURCE_TYPES_URL,
} from 'features/admin/features/sources/paths'
import {
  ADMIN_URL,
  HUB_INTEGRITY_URL,
  EXPORT_GROUPS_URL,
  FEED_AUDIT_URL,
  FEATURE_FLAGS_URL,
  INGESTION_RUNS_URL,
  NLP_PROMPT_CATALOG_URL,
  PROJECT_SCHEDULES_URL,
  SYNDICATED_SOURCE_PROMOTIONS_URL,
} from 'features/admin/paths'
import { lazyComponent, lazyDefault } from 'routes/lazy-route'

// Top-level account-global admin area. Registered as a SIBLING of the project
// routes (NOT under `projectPath()`) — the shell gates on `feature_flags:read`.
export const adminRoutes: RouteObject[] = [
  {
    path: ADMIN_URL,
    lazy: lazyDefault(() => import('features/admin/Container')),
    handle: { featureKey: ADMIN_URL },
    children: [
      {
        path: RBAC_URL,
        children: [
          {
            index: true,
            lazy: lazyComponent(
              () => import('features/admin/features/rbac/Container'),
              'RbacIndexRedirect',
            ),
          },
          {
            path: RBAC_SCOPES_URL,
            lazy: lazyComponent(
              () => import('features/admin/features/rbac/Container'),
              'RbacScopesContainer',
            ),
          },
          {
            path: RBAC_ASSIGNMENTS_URL,
            lazy: lazyComponent(
              () => import('features/admin/features/rbac/Container'),
              'RbacAssignmentsContainer',
            ),
          },
        ],
      },
      {
        index: true,
        lazy: lazyComponent(
          () => import('features/admin/Container'),
          'AdminHomeContainer',
        ),
      },
      {
        path: SYNDICATED_SOURCE_PROMOTIONS_URL,
        lazy: lazyComponent(
          () =>
            import('features/admin/features/syndicated-source-promotions/Container'),
          'SyndicatedSourcePromotionsContainer',
        ),
      },
      {
        path: FEATURE_FLAGS_URL,
        children: [
          {
            index: true,
            lazy: lazyComponent(
              () => import('features/admin/features/feature-flags/Container'),
              'FeatureFlagsContainer',
            ),
          },
          {
            path: FEATURE_FLAG_DETAIL_SEGMENT,
            lazy: lazyComponent(
              () => import('features/admin/features/feature-flags/Container'),
              'FeatureFlagDetailContainer',
            ),
          },
        ],
      },
      {
        path: NLP_PROMPT_CATALOG_URL,
        children: [
          {
            index: true,
            lazy: lazyComponent(
              () =>
                import('features/admin/features/nlp-prompt-catalog/Container'),
              'NlpPromptCatalogContainer',
            ),
          },
          {
            path: FAMILY_CREATE_SEGMENT,
            lazy: lazyComponent(
              () =>
                import('features/admin/features/nlp-prompt-catalog/Container'),
              'NlpPromptFamilyCreateContainer',
            ),
          },
          {
            path: FAMILY_DETAIL_SEGMENT,
            lazy: lazyComponent(
              () =>
                import('features/admin/features/nlp-prompt-catalog/Container'),
              'NlpPromptFamilyDetailContainer',
            ),
          },
        ],
      },
      {
        path: EXPORT_GROUPS_URL,
        lazy: lazyComponent(
          () => import('features/admin/features/export-groups/Container'),
          'AdminExportGroupsContainer',
        ),
      },
      {
        path: SOURCES_URL,
        children: [
          {
            index: true,
            lazy: lazyComponent(
              () => import('features/admin/features/sources/Container'),
              'SourcesIndexRedirect',
            ),
          },
          {
            path: SOURCE_CHANNELS_URL,
            lazy: lazyComponent(
              () => import('features/admin/features/sources/Container'),
              'SourceChannelsContainer',
            ),
          },
          {
            path: SOURCE_CHANNEL_TYPES_URL,
            lazy: lazyComponent(
              () => import('features/admin/features/sources/Container'),
              'SourceChannelTypesContainer',
            ),
          },
          {
            path: SOURCE_TYPES_URL,
            lazy: lazyComponent(
              () => import('features/admin/features/sources/Container'),
              'SourceTypesContainer',
            ),
          },
          {
            path: SOURCE_PROVIDERS_URL,
            lazy: lazyComponent(
              () => import('features/admin/features/sources/Container'),
              'SourceProvidersContainer',
            ),
          },
        ],
      },
      {
        path: SOURCE_CATALOG_URL,
        lazy: lazyComponent(
          () => import('features/admin/features/source-catalog/Container'),
          'SourceCatalogContainer',
        ),
      },
      {
        path: PROJECT_SCHEDULES_URL,
        lazy: lazyComponent(
          () => import('features/admin/features/project-schedules/Container'),
          'ProjectSchedulesContainer',
        ),
      },
      {
        path: FEED_AUDIT_URL,
        lazy: lazyComponent(
          () => import('features/admin/features/feed-audit/Container'),
          'FeedAuditContainer',
        ),
        children: [
          {
            index: true,
            lazy: lazyComponent(
              () => import('features/admin/features/feed-audit/Container'),
              'FeedAuditIndexRedirect',
            ),
          },
          {
            path: INCOMPLETE_PROJECTS_URL,
            lazy: lazyComponent(
              () =>
                import('features/admin/features/feed-audit/features/incomplete/Container'),
              'IncompleteProjectsContainer',
            ),
          },
          {
            path: EXPORTS_URL,
            lazy: lazyComponent(
              () =>
                import('features/admin/features/feed-audit/features/exports/Container'),
              'ExportsAuditContainer',
            ),
          },
          {
            path: VENDOR_DELIVERIES_URL,
            lazy: lazyComponent(
              () =>
                import('features/admin/features/feed-audit/features/vendor-deliveries/Container'),
              'VendorDeliveriesContainer',
            ),
          },
          {
            path: URL_SUBMISSIONS_URL,
            lazy: lazyComponent(
              () =>
                import('features/admin/features/feed-audit/features/url-submissions/Container'),
              'UrlSubmissionsContainer',
            ),
          },
          {
            path: RATINGS_ANOMALIES_URL,
            lazy: lazyComponent(
              () =>
                import('features/admin/features/feed-audit/features/ratings-anomalies/Container'),
              'RatingsAnomaliesContainer',
            ),
            children: [
              {
                path: RATINGS_ANOMALY_DETAIL_SEGMENT,
                lazy: lazyComponent(
                  () =>
                    import('features/admin/features/feed-audit/features/ratings-anomalies/Container'),
                  'RatingsAnomalyDetailContainer',
                ),
              },
            ],
          },
        ],
      },
      {
        path: HUB_INTEGRITY_URL,
        children: [
          {
            index: true,
            lazy: lazyComponent(
              () => import('features/admin/features/data-integrity/Container'),
              'IntegrityHubsContainer',
            ),
          },
          {
            path: HUB_DETAIL_SEGMENT,
            lazy: lazyComponent(
              () => import('features/admin/features/data-integrity/Container'),
              'IntegrityHubContainer',
            ),
          },
        ],
      },
      {
        path: INGESTION_RUNS_URL,
        lazy: lazyComponent(
          () => import('features/admin/features/ingestion-runs/Container'),
          'IngestionRunsContainer',
        ),
      },
    ],
  },
  // ItemEyes opens OUTSIDE the admin shell: the sidebar lists it, but the page
  // takes the whole window; a small link under its logo goes back to admin.
  // The screen is the path, `/admin/itemeyes/<section>/<tab>[/<schemaId>]`;
  // the bare page and any unknown segment redirect to a real screen.
  {
    path: `${ADMIN_URL}/${ITEMEYES_URL}`,
    handle: { featureKey: ADMIN_URL },
    children: [
      {
        index: true,
        lazy: lazyComponent(
          () => import('features/admin/features/itemeyes/Container'),
          'ItemEyesIndexRedirect',
        ),
      },
      {
        path: ITEMEYES_SCREEN_SEGMENT,
        lazy: lazyComponent(
          () => import('features/admin/features/itemeyes/Container'),
          'ItemEyesContainer',
        ),
      },
    ],
  },
]
